import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Shield, RefreshCw, X, Copy, CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TwoFASection({ token }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState("idle");
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [disablePassword, setDisablePassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch { setStatus({ enabled: false }); }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startSetup = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setStep("setup");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la configuration");
    } finally { setIsLoading(false); }
  };

  const enableTwoFA = async () => {
    if (!verifyCode.trim()) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/2fa/enable`, { code: verifyCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecoveryCodes(res.data.recovery_codes);
      setStep("codes");
      toast.success("2FA activé avec succès");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
    } finally { setIsLoading(false); }
  };

  const disableTwoFA = async () => {
    if (!disablePassword.trim()) return;
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/2fa/disable`, { password: disablePassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("2FA désactivé");
      localStorage.removeItem("device_trust_token");
      setStep("idle");
      setDisablePassword("");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mot de passe incorrect");
    } finally { setIsLoading(false); }
  };

  const regenerateCodes = async () => {
    if (!disablePassword.trim()) return;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/2fa/regenerate-codes`, { password: disablePassword }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecoveryCodes(res.data.recovery_codes);
      setStep("codes");
      setDisablePassword("");
      toast.success("Codes de récupération régénérés");
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setIsLoading(false); }
  };

  const copyAllCodes = () => {
    const text = recoveryCodes.join("\n");
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Codes copiés");
    } catch { toast.error("Erreur de copie"); }
  };

  if (!status) return null;

  return (
    <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-xl p-4 sm:p-6" data-testid="2fa-section">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status.enabled ? "bg-green-500/20" : "bg-zinc-800"}`}>
          <Shield className={`w-5 h-5 ${status.enabled ? "text-green-400" : "text-zinc-500"}`} />
        </div>
        <div className="flex-1">
          <h2 className="text-white font-medium flex items-center gap-2">
            Authentification à deux facteurs
            {status.enabled && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">Activé</Badge>
            )}
          </h2>
          <p className="text-xs text-zinc-500">
            {status.enabled
              ? `${status.remaining_recovery_codes} code(s) de récupération restant(s)`
              : "Ajoutez une couche de sécurité supplémentaire"}
          </p>
        </div>
      </div>

      {/* NOT ENABLED */}
      {!status.enabled && step === "idle" && (
        <Button onClick={startSetup} disabled={isLoading}
          className="w-full bg-green-600 hover:bg-green-700 text-white text-sm"
          data-testid="enable-2fa-btn">
          {isLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
          Activer le 2FA
        </Button>
      )}

      {/* SETUP */}
      {step === "setup" && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-zinc-300 mb-3">Scannez ce QR code avec votre application d'authentification</p>
            <p className="text-xs text-zinc-500 mb-4">(Google Authenticator, Authy, etc.)</p>
            {qrCode && (
              <div className="inline-block bg-white p-3 rounded-lg">
                <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" data-testid="2fa-qr-code" />
              </div>
            )}
            <div className="mt-3">
              <p className="text-[10px] text-zinc-600 mb-1">Ou entrez manuellement cette clé :</p>
              <code className="text-xs font-mono text-amber-400 bg-zinc-800 px-3 py-1.5 rounded select-all" data-testid="2fa-secret-key">
                {secret}
              </code>
            </div>
          </div>
          <hr className="border-zinc-800" />
          <div>
            <label className="text-xs text-zinc-400 mb-2 block">Entrez le code à 6 chiffres affiché dans votre app</label>
            <Input
              type="text" value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="bg-zinc-900 border-zinc-700 text-white text-sm font-mono text-center text-lg tracking-widest"
              maxLength={6}
              data-testid="2fa-setup-code-input"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setStep("idle"); setVerifyCode(""); }}
              variant="outline" className="flex-1 border-zinc-700 text-zinc-400 text-sm">Annuler</Button>
            <Button onClick={enableTwoFA} disabled={isLoading || verifyCode.length !== 6}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm" data-testid="2fa-confirm-enable-btn">
              {isLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
              Activer
            </Button>
          </div>
        </div>
      )}

      {/* RECOVERY CODES */}
      {step === "codes" && recoveryCodes.length > 0 && (
        <div className="space-y-4">
          <div className="p-3 border border-amber-500/30 bg-amber-500/5 rounded-lg">
            <p className="text-amber-400 text-sm font-medium mb-1">Sauvegardez vos codes de récupération</p>
            <p className="text-xs text-zinc-400">Ces codes vous permettront de vous connecter si vous perdez l'accès à votre application d'authentification. Chaque code ne peut être utilisé qu'une seule fois.</p>
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="2fa-recovery-codes">
            {recoveryCodes.map((code, i) => (
              <div key={i} className="bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-center">
                <code className="font-mono text-sm text-white tracking-wider">{code}</code>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={copyAllCodes} variant="outline" className="flex-1 border-zinc-700 text-zinc-300 text-sm" data-testid="copy-recovery-codes-btn">
              <Copy className="w-4 h-4 mr-2" />Copier tous les codes
            </Button>
            <Button onClick={() => { setStep("idle"); setRecoveryCodes([]); }} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black text-sm">
              J'ai sauvegardé mes codes
            </Button>
          </div>
        </div>
      )}

      {/* ENABLED - Management */}
      {status.enabled && step === "idle" && (
        <div className="space-y-3">
          {status.remaining_recovery_codes <= 2 && (
            <div className="p-3 border border-red-500/30 bg-red-500/5 rounded-lg">
              <p className="text-red-400 text-xs">Il ne vous reste que {status.remaining_recovery_codes} code(s) de récupération. Pensez à les régénérer.</p>
            </div>
          )}
          <Button onClick={() => setStep("regenerate")} variant="outline" className="w-full border-zinc-700 text-zinc-300 text-sm" data-testid="regenerate-codes-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Régénérer les codes de récupération
          </Button>
          <Button onClick={() => setStep("disable")} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm" data-testid="disable-2fa-btn">
            <X className="w-4 h-4 mr-2" />Désactiver le 2FA
          </Button>
        </div>
      )}

      {/* DISABLE */}
      {step === "disable" && (
        <div className="space-y-4">
          <div className="p-3 border border-red-500/30 bg-red-500/5 rounded-lg">
            <p className="text-red-400 text-sm font-medium">Désactiver la double authentification ?</p>
            <p className="text-xs text-zinc-400 mt-1">Votre compte sera moins sécurisé.</p>
          </div>
          <div>
            <label className="text-xs text-zinc-400 mb-2 block">Confirmez avec votre mot de passe</label>
            <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Mot de passe" className="bg-zinc-900 border-zinc-700 text-white text-sm" data-testid="disable-2fa-password" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setStep("idle"); setDisablePassword(""); }} variant="outline" className="flex-1 border-zinc-700 text-zinc-400 text-sm">Annuler</Button>
            <Button onClick={disableTwoFA} disabled={isLoading || !disablePassword}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm" data-testid="confirm-disable-2fa-btn">
              {isLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
              Désactiver
            </Button>
          </div>
        </div>
      )}

      {/* REGENERATE */}
      {step === "regenerate" && (
        <div className="space-y-4">
          <p className="text-sm text-zinc-300">Les anciens codes seront invalidés.</p>
          <div>
            <label className="text-xs text-zinc-400 mb-2 block">Confirmez avec votre mot de passe</label>
            <Input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Mot de passe" className="bg-zinc-900 border-zinc-700 text-white text-sm" data-testid="regenerate-codes-password" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => { setStep("idle"); setDisablePassword(""); }} variant="outline" className="flex-1 border-zinc-700 text-zinc-400 text-sm">Annuler</Button>
            <Button onClick={regenerateCodes} disabled={isLoading || !disablePassword}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-black text-sm" data-testid="confirm-regenerate-btn">
              {isLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Régénérer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
